import { useMemo, useCallback, useRef } from "react";
import { selectionToTeamEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  FieldTrigger,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useEntitySelectorSelections,
  type EntitySelectorConfig,
  type EntitySelectorContentSize,
  type EntitySelectorVariant,
  type EntitySelection,
  type IconName,
  useSelectorKeyboardNav,
} from "@salestrak/ui";
import { useTeamSelection } from "#app/components/entity-selectors/use-team-selection";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

export type TeamSelectorProps = Omittable<{
  value?: string | string[] | null;
  onValueChange?: (ids: string[]) => void;
  exclude?: string[];
  maxSelections?: number;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  /** The trigger's accessible name, when a visible label names the field
   *  ("Add to team"). */
  "aria-label"?: string;
  size?: SelectorSize;
  disabled?: boolean;
  chips?: boolean;
  summary?: boolean;
  icon?: IconName;
  fill?: boolean;
  contentFit?: boolean;
  contentSize?: EntitySelectorContentSize;
  /** Trigger dressing passthroughs for the value trigger: a form field wears
   *  `outline` with the chevron; property rows keep the ghost default. */
  buttonVariant?: React.ComponentProps<typeof SelectionEntityTrigger>["buttonVariant"];
  shape?: React.ComponentProps<typeof SelectionEntityTrigger>["shape"];
  showChevron?: boolean;
  /** The Select field's exact dress, for a picker sitting among selects (a
   *  settings row). See `SelectionEntityTrigger`. */
  field?: boolean;
  /** The Select field's exact anatomy holding the team NAMES as plain text
   *  (no tile, no avatar) — for a picker sitting beside plain selects in a
   *  list row, where an entity tag reads as a different control. Empty
   *  shows `placeholder`. */
  plain?: boolean;
  /** `false` drops the search field and the Selected band, for a short fixed
   *  list the viewer already knows (their own teams): the popup is the rows
   *  alone, and the trigger states the selection. The popup itself takes
   *  focus and hears the arrow keys and Enter. @default true */
  search?: boolean;
  children?: React.ReactNode;
}>;

/** The Select field's anatomy with the chosen teams' names as plain text. */
function TeamSelectorPlainFieldTrigger({
  placeholder,
  "aria-label": ariaLabel,
  disabled,
  fill,
}: Omittable<{
  placeholder: string;
  "aria-label"?: string;
  disabled?: boolean;
  fill?: boolean;
}>) {
  const selections = useEntitySelectorSelections();
  // One team reads by name; more read as a count — the names would only
  // truncate into each other in a field — and the count carries a tooltip
  // that lists them.
  const many = selections.length > 1;
  const value =
    selections.length === 0
      ? undefined
      : many
        ? `${selections.length} teams`
        : selections[0]!.entity.title;
  const field = (
    <FieldTrigger
      value={value}
      placeholder={placeholder}
      width={fill ? "full" : "auto"}
      // The column that asked for `fill` owns the width, floor included.
      className={fill ? "min-w-0" : undefined}
      disabled={disabled}
    />
  );
  // ONE tree whatever the count: switching a tooltip wrapper in or out
  // remounts the trigger under an OPEN popover, which then loses its anchor
  // and lands at the viewport's corner. The tooltip trigger sits INSIDE the
  // popover trigger's render chain, directly around the leaf — the other
  // order never opens.
  return (
    <Tooltip disabled={!many}>
      <EntitySelectorTrigger
        aria-label={ariaLabel ?? placeholder}
        disabled={disabled}
        render={<TooltipTrigger render={field} />}
      />
      {/* One comma-separated line; the popup's own max-w-xs wraps a long
          list and fits the bubble to its widest line. */}
      <TooltipPopup side="top">
        {selections.map((sel) => sel.entity.title).join(", ")}
      </TooltipPopup>
    </Tooltip>
  );
}

export function TeamSelector({
  value,
  onValueChange,
  exclude,
  maxSelections,
  variant,
  placeholder = "Select teams...",
  "aria-label": ariaLabel,
  size,
  disabled,
  chips,
  summary,
  icon,
  fill,
  contentFit,
  contentSize,
  buttonVariant,
  shape,
  showChevron,
  field,
  plain,
  search = true,
  children,
}: TeamSelectorProps) {
  const { pool, selections, toIds } = useTeamSelection({ value, exclude });
  // Without a search field the popup is the list's keyboard home.
  const { ref: selectorRef, inputProps } = useSelectorKeyboardNav();
  const contentRef = useRef<HTMLDivElement>(null);

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections,
      searchPlaceholder: "Search teams...",
      emptyMessage: "No teams found",
      noItemsMessage: "No teams yet",
    }),
    [pool, maxSelections],
  );

  // One pick replaces the last; a multi-select carries the picks it cannot
  // show yet (`toIds`).
  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange?.(maxSelections === 1 ? next.map((s) => s.id) : toIds(next));
    },
    [onValueChange, maxSelections, toIds],
  );

  return (
    <EntitySelector
      title="Teams"
      ref={selectorRef}
      variant={variant}
      config={config}
      value={selections}
      onValueChange={handleValueChange}
    >
      {children ?? (
        <>
          {plain ? (
            <TeamSelectorPlainFieldTrigger
              placeholder={placeholder}
              aria-label={ariaLabel}
              disabled={disabled}
              fill={fill}
            />
          ) : chips || summary ? (
            <EntitySelectorTrigger
              placeholder={placeholder}
              aria-label={ariaLabel}
              size={toEntityTriggerSize(size)}
              disabled={disabled}
              chips={chips}
              summary={summary}
              fill={fill}
              icon={icon}
            />
          ) : (
            <SelectionEntityTrigger
              toEntity={selectionToTeamEntity}
              placeholder={placeholder}
              aria-label={ariaLabel}
              title="Teams"
              emptyIcon={icon}
              size={size}
              disabled={disabled}
              fill={fill}
              buttonVariant={buttonVariant}
              shape={shape}
              showChevron={showChevron}
              field={field}
            />
          )}
          {search ? (
            <EntitySelectorContent size={contentSize} fit={contentFit}>
              <EntitySelectorInput />
              <EntitySelectorPool value={pool.id} />
            </EntitySelectorContent>
          ) : (
            <EntitySelectorContent
              ref={contentRef}
              initialFocus={contentRef}
              onKeyDown={inputProps.onKeyDown}
              size={contentSize}
              fit={contentFit}
            >
              {/* The rows are the whole popup; the host's label names them. */}
              <EntitySelectorPool value={pool.id} hideLabel />
            </EntitySelectorContent>
          )}
        </>
      )}
    </EntitySelector>
  );
}
